'use client'

import { datadogLogs, StatusType } from '@datadog/browser-logs'
import { DataDogBrowserLogsTransport } from '@loglayer/transport-datadog-browser-logs'
import { LogLayer, type LogLayerTransport } from 'loglayer'

import type { ClientLoggerInitConfig } from '../types'
import { LOGLAYER_DEFAULT_CONFIG } from './constants'
import { LogLevel, type Logger } from './types'
import { LoggerWrapper } from './wrapper'

export interface ClientLoggerTransportFactories {
    console?: () => LogLayerTransport
    http?: () => LogLayerTransport
}

// Map our LogLevel enum to Datadog's StatusType
const mapLogLevelToStatusType = (logLevel: LogLevel): StatusType => {
    switch (logLevel) {
        case LogLevel.trace:
        case LogLevel.debug:
            return StatusType.debug
        case LogLevel.info:
            return StatusType.info
        case LogLevel.warn:
            return StatusType.warn
        case LogLevel.error:
            return StatusType.error
        case LogLevel.fatal:
            return StatusType.error
    }
}

// Uses Datadog browser SDK for console output and optional log shipping
const makeClientLogLayer = (
    config: ClientLoggerInitConfig,
    factories?: ClientLoggerTransportFactories,
): LogLayer => {
    const transports = []

    // Console transport - we always use this to log to browser console
    const consoleTransport =
        factories?.console?.() ??
        new DataDogBrowserLogsTransport({
            logger: datadogLogs.createLogger('console', {
                level: mapLogLevelToStatusType(config.logs.logLevel),
                handler: 'console',
            }),
        })
    transports.push(consoleTransport)

    // HTTP transport - ships logs to Datadog when enabled
    if (config.datadog.logs.enabled) {
        const httpTransport =
            factories?.http?.() ??
            new DataDogBrowserLogsTransport({
                enabled: config.datadog.logs.enabled,
                logger: datadogLogs.createLogger('http', {
                    level: mapLogLevelToStatusType(config.datadog.logs.logLevel),
                    handler: 'http',
                }),
            })
        transports.push(httpTransport)
    }

    return new LogLayer({
        ...LOGLAYER_DEFAULT_CONFIG,
        transport: transports,
    }).withContext({ name: 'client' })
}

export function initializeClientLogger(
    config: ClientLoggerInitConfig,
    factories?: ClientLoggerTransportFactories,
): Logger {
    const logLayer = makeClientLogLayer(config, factories).withContext({ module: 'root' })
    return new LoggerWrapper(logLayer)
}
